import React, { useState } from "react";
import { Phone, MapPin, Clock, ArrowRight, CheckCircle2 } from "lucide-react";
import { CONFIG, T, SERVICES, telLink, waLink } from "../../data/content";
import { useLang } from "../../context/i18n";
import { Reveal } from "./Motion";
import { track } from "./Chrome";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LocationContact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", service: "", preferred_date: "", message: "", consent: false });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errors, setErrors] = useState({});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = true;
    if (!/^[0-9+\-\s]{6,15}$/.test(form.phone.trim())) e.phone = true;
    if (!form.consent) e.consent = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      setStatus("success");
      track("enquiry_submit");
      window.gtag && window.gtag("event", "conversion", { send_to: CONFIG.adsId });
      setForm({ name: "", phone: "", service: "", preferred_date: "", message: "", consent: false });
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <Reveal>
          <p className="font-head text-sm font-bold uppercase tracking-widest text-[var(--red)]">07 — {t(T.contactTitle)}</p>
          <h2 className="mt-3 font-head text-3xl font-extrabold tracking-tight text-[var(--navy)] sm:text-4xl lg:text-5xl">{t(T.contactTitle)}</h2>
        </Reveal>

        <div className="mt-12 grid gap-8 lg:grid-cols-2">
          {/* Left: info + map */}
          <Reveal>
            <div className="rounded-3xl border border-slate-200 bg-[var(--bg-soft)] p-7">
              <h3 className="font-head text-xl font-extrabold text-[var(--navy)]">Jan Seva Hospital</h3>
              <div className="mt-4 flex items-start gap-3 text-[var(--slate)]">
                <MapPin size={20} className="mt-0.5 shrink-0 text-[var(--red)]" />
                <p className="text-sm">Dr. Kishori Mohan Complex<br />Sikariya More, Gaya, Bihar</p>
              </div>
              <div className="mt-4 flex items-start gap-3 text-[var(--slate)]">
                <Phone size={20} className="mt-0.5 shrink-0 text-[var(--navy)]" />
                <p className="text-sm">
                  <a href={telLink(CONFIG.phonePrimary)} className="font-semibold hover:text-[var(--navy)]">+91 {CONFIG.phonePrimary}</a><br />
                  <a href={telLink(CONFIG.phoneSecondary)} className="font-semibold hover:text-[var(--navy)]">+91 {CONFIG.phoneSecondary}</a>
                </p>
              </div>
              <div className="mt-4 flex items-start gap-3 text-[var(--slate)]">
                <Clock size={20} className="mt-0.5 shrink-0 text-[var(--green)]" />
                <div className="text-sm">
                  <p className="font-bold text-[var(--navy)]">{t(T.openingHours)}</p>
                  <p>{t(CONFIG.hours.emergency)}</p>
                  <p>{t(CONFIG.hours.opd)}</p>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <a href={telLink(CONFIG.phonePrimary)} data-testid="contact-call" className="inline-flex items-center gap-2 rounded-full bg-[var(--navy)] px-4 py-2.5 text-sm font-bold text-white transition-all hover:scale-[1.03]"><Phone size={16} /> {t(T.call)}</a>
                <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="inline-flex items-center gap-2 rounded-full bg-[var(--green)] px-4 py-2.5 text-sm font-bold text-white transition-all hover:scale-[1.03]"><i className="fa-brands fa-whatsapp" aria-hidden="true"></i> {t(T.whatsapp)}</a>
                <a href={CONFIG.mapsLink} target="_blank" rel="noopener noreferrer" data-testid="contact-directions" className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-sm font-bold text-[var(--navy)] ring-1 ring-slate-300 transition-all hover:scale-[1.03]">{t(T.getDirections)} <ArrowRight size={16} /></a>
              </div>

              <div className="mt-6 overflow-hidden rounded-2xl ring-1 ring-slate-200">
                {CONFIG.mapsEmbed && CONFIG.mapsEmbed.startsWith("http") ? (
                  <iframe title="Jan Seva Hospital location map" src={CONFIG.mapsEmbed} width="100%" height="240" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                ) : (
                  <div className="flex h-56 flex-col items-center justify-center bg-white p-4 text-center text-slate-400">
                    <MapPin size={32} />
                    <p className="mt-2 text-xs font-semibold">⬅ Insert Google Maps embed URL in <code>content.js</code> (CONFIG.mapsEmbed)</p>
                  </div>
                )}
              </div>
            </div>
          </Reveal>

          {/* Right: form */}
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm">
              <h3 className="font-head text-xl font-extrabold text-[var(--navy)]">{t(T.sendEnquiry)}</h3>
              {status === "success" ? (
                <div data-testid="form-success" className="mt-6 flex flex-col items-center rounded-2xl bg-green-50 p-8 text-center">
                  <CheckCircle2 size={44} className="text-[var(--green)]" />
                  <p className="mt-3 text-sm font-semibold text-[var(--navy)]">{t(T.successMsg)}</p>
                  <button onClick={() => setStatus("idle")} className="mt-4 text-sm font-bold text-[var(--navy)] underline">OK</button>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-5 space-y-4" data-testid="contact-form" noValidate>
                  <Field label={t(T.formName)} req>
                    <input data-testid="form-name" value={form.name} onChange={(e) => set("name", e.target.value)}
                      className={inp(errors.name)} placeholder={t(T.formName)} />
                  </Field>
                  <Field label={t(T.formPhone)} req>
                    <input data-testid="form-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} inputMode="tel"
                      className={inp(errors.phone)} placeholder="e.g. 98765 43210" />
                  </Field>
                  <Field label={t(T.formService)}>
                    <select data-testid="form-service" value={form.service} onChange={(e) => set("service", e.target.value)} className={inp(false)}>
                      <option value="">{t(T.chooseService)}</option>
                      {SERVICES.map((s, i) => <option key={i} value={t(s).t}>{t(s).t}</option>)}
                    </select>
                  </Field>
                  <Field label={t(T.formDate)}>
                    <input data-testid="form-date" type="date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} className={inp(false)} />
                  </Field>
                  <Field label={t(T.formMessage)}>
                    <textarea data-testid="form-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={3} className={inp(false)} placeholder={t(T.formMessage)} />
                  </Field>

                  <label className="flex items-start gap-3 text-sm text-[var(--slate)]">
                    <input data-testid="form-consent" type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)}
                      className={`mt-0.5 h-5 w-5 shrink-0 rounded ${errors.consent ? "ring-2 ring-[var(--red)]" : ""}`} />
                    <span>{t(T.formConsent)}</span>
                  </label>
                  <p className="text-xs text-slate-400">{t(T.noSensitive)}</p>

                  {status === "error" && <p data-testid="form-error" className="text-sm font-semibold text-[var(--red)]">{t(T.errorMsg)}</p>}

                  <button type="submit" disabled={status === "loading"} data-testid="form-submit"
                    className="w-full rounded-full bg-[var(--navy)] py-3.5 text-sm font-bold text-white transition-all hover:scale-[1.02] hover:bg-[var(--navy-light)] disabled:opacity-60">
                    {status === "loading" ? t(T.submitting) : t(T.submit)}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const inp = (err) =>
  `w-full rounded-xl border bg-white px-4 py-3 text-sm text-[var(--navy)] outline-none transition-colors focus:border-[var(--navy)] ${err ? "border-[var(--red)]" : "border-slate-300"}`;

const Field = ({ label, req, children }) => (
  <div>
    <label className="mb-1.5 block text-sm font-semibold text-[var(--navy)]">{label}{req && <span className="text-[var(--red)]"> *</span>}</label>
    {children}
  </div>
);
